import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../api';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  user: string | null;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is already authenticated on component mount
  useEffect(() => {
    console.log('AuthContext useEffect - checking localStorage');
    const savedAuth = localStorage.getItem('simplepos-auth');
    console.log('AuthContext - savedAuth:', savedAuth);
    
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth);
        console.log('AuthContext - parsed authData:', authData);
        if (authData.isAuthenticated && authData.user && authData.accessToken) {
          console.log('AuthContext - setting authenticated state');
          setIsAuthenticated(true);
          setUser(authData.user);
        } else {
          // Clear invalid auth data
          localStorage.removeItem('simplepos-auth');
        }
      } catch (error) {
        console.error('Error parsing saved auth:', error);
        localStorage.removeItem('simplepos-auth');
      }
    }
    
    console.log('AuthContext - setting isLoading to false');
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    console.log('AuthContext login called with:', username, password);
    
    try {
      const response = await api.login(username, password);
      
      if (response.success) {
        console.log('Login successful, setting authentication state');
        setIsAuthenticated(true);
        setUser(username);
        
        // Save authentication state to localStorage with tokens
        localStorage.setItem('simplepos-auth', JSON.stringify({
          isAuthenticated: true,
          user: username,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          timestamp: Date.now()
        }));
        
        console.log('Authentication state updated, returning true');
        return true;
      } else {
        console.log('Login failed:', response.message);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const response = await api.changePassword(user, currentPassword, newPassword);
      return response.success;
    } catch (error) {
      console.error('Change password error:', error);
      return false;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    const authData = localStorage.getItem('simplepos-auth');
    if (!authData) return false;
    
    try {
      const parsed = JSON.parse(authData);
      if (!parsed.refreshToken) return false;
      
      const response = await api.refreshToken(parsed.refreshToken);
      
      if (response.success && response.accessToken) {
        // Update the access token in localStorage
        localStorage.setItem('simplepos-auth', JSON.stringify({
          ...parsed,
          accessToken: response.accessToken,
          timestamp: Date.now()
        }));
        return true;
      } else {
        // Refresh failed, clear auth data
        localStorage.removeItem('simplepos-auth');
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      localStorage.removeItem('simplepos-auth');
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  };

  const logout = async () => {
    const authData = localStorage.getItem('simplepos-auth');
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        if (parsed.refreshToken) {
          // Call logout endpoint to invalidate refresh token
          await api.logout(parsed.refreshToken);
        }
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('simplepos-auth');
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    login,
    logout,
    user,
    changePassword,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
